import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  deleteAccount,
  saveAccount,
} from '@/renderer/services/accountingService';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import type { SaveAccountRequest } from '@/renderer/types/accounting';

export function useSaveAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveAccountRequest) => {
      const result = await saveAccount(payload);
      if (!result.success)
        throw new Error(result.error ?? 'Failed to save account');
      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting.all });
      apiSuccess(payload.id ? 'Account updated.' : 'Account created.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      accountId,
    }: {
      companyId: number;
      accountId: string;
    }) => {
      const result = await deleteAccount(companyId, accountId);
      if (!result.success)
        throw new Error(result.error ?? 'Failed to delete account');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting.all });
      apiSuccess('Account deleted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}
